export const BRIEF_STATUSES = ['Pending', 'Draft', 'Submitted', 'In review', 'Assigned', 'In progress', 'Done', 'Archived'];

export const BRIEF_VISIBILITY = {
  ONLY_ME: 'only_me',
  TEAM: 'team',
};

export function defaultVisibilityForStatus(status) {
  return String(status || '').toLowerCase() === 'draft'
    ? BRIEF_VISIBILITY.ONLY_ME
    : BRIEF_VISIBILITY.TEAM;
}

export function vanityStartPath(slug) {
  return `/briefs/start/${slug}`;
}

export function vanityBuildPath(slug, query = {}) {
  const base = `/briefs/start/${slug}/build`;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && String(value).trim() !== '') {
      params.set(key, String(value));
    }
  });
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export function vanityTemplatePath(slug, templateId) {
  return `/briefs/start/${encodeURIComponent(slug)}/template/${encodeURIComponent(templateId)}`;
}

const TEMPLATE_DRAFT_PREFIX = 'brief-vanity-template-draft:';

export function persistTemplateDraft(slug, brief) {
  if (!slug || !brief || typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(`${TEMPLATE_DRAFT_PREFIX}${slug}`, JSON.stringify(brief));
  } catch {
    /* ignore */
  }
}

export function loadTemplateDraft(slug) {
  if (!slug || typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(`${TEMPLATE_DRAFT_PREFIX}${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearTemplateDraft(slug) {
  if (!slug || typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(`${TEMPLATE_DRAFT_PREFIX}${slug}`);
  } catch {
    /* ignore */
  }
}

export function getDefaultPdpBrief() {
  return {
    title: 'PDP refresh',
    summary: '',
    sections: [
      {
        typeId: 'brand_skus',
        content: '1. SKU one\n2. SKU two',
        visible: true,
      },
      {
        typeId: 'objective',
        content: 'Create new PDP assets for the new items that maintain the existing PDP design structure.',
        visible: true,
      },
      {
        typeId: 'deliverables',
        content: '1. Alcohol Content · 1:1\n2. Family Shot · 1:1\n3. Flavor Name · 1:1\n4. Family Car Shot · 1:1\n5. Pour Image · 1:1\n6. Key Visual · 1:1',
        visible: true,
      },
      {
        typeId: 'creative_direction',
        content: '1. Alcohol Content\n   • Reflect new packaging colors.\n2. Family Shot\n   • Leverage POS family shot asset; swap in the new SKUs.\n3. Flavor Name\n   • Mirror the bottom panel of the packaging file. Include sub-heading + flavor copy.\n4. Family Car Shot\n   • Explore photoshopping the new SKUs next to the existing line-up.\n5. Pour Image\n   • Lead with "Intense Flavors" + a one-line flavor descriptor.\n6. Key Visual\n   • Update striping color, % ABV color, and bottle shot.',
        visible: true,
      },
      {
        typeId: 'mandatories',
        content: 'Legal copy per packaging file. © brand line on every asset.',
        visible: true,
      },
      {
        typeId: 'assets_included',
        content: '- Packaging file\n- Pantone colors\n- Striping file\n- POS file\n- Key visual\n- Photoshoot images',
        visible: true,
      },
      {
        typeId: 'brand_guidelines',
        content: '',
        visible: true,
      },
      {
        typeId: 'schedule',
        content: '',
        visible: true,
      },
      {
        typeId: 'audience',
        content: '',
        visible: false,
      },
      {
        typeId: 'key_message',
        content: '',
        visible: false,
      },
      {
        typeId: 'tone_voice',
        content: '',
        visible: false,
      },
      {
        typeId: 'budget',
        content: '',
        visible: false,
      },
      {
        typeId: 'success_metrics',
        content: '',
        visible: false,
      },
    ],
    hiddenSectionIds: ['audience', 'key_message', 'tone_voice', 'budget', 'success_metrics'],
    references: [],
    payload: {
      template: 'pdp',
      intake_template: 'pdp',
      section_labels: { ...PDP_SECTION_LABELS },
    },
  };
}

export const INTAKE_MAGIC_LINK_STORAGE_PREFIX = 'brief-vanity-magic-link:';
export const INTAKE_LINK_TOKEN_STORAGE_PREFIX = 'brief-vanity-intake-link-token:';
export const INTAKE_EMAIL_SENT_STORAGE_PREFIX = 'brief-vanity-email-sent:';

export function briefSubmittedConfirmationPath(slug) {
  return `/briefs/start/${slug}/confirmation`;
}

const SUBMITTED_PAYLOAD_PREFIX = 'brief-submitted-payload:';
const SUBMITTED_IDENTITY_PREFIX = 'brief-submitted-identity:';

export function persistSubmittedBrief(slug, payload) {
  try {
    sessionStorage.setItem(
      `${SUBMITTED_PAYLOAD_PREFIX}${slug}`,
      JSON.stringify(payload),
    );
  } catch {
    /* ignore */
  }
}

export function persistSubmittedIdentity(slug, identity) {
  const email = (identity && identity.email ? String(identity.email) : '').trim();
  if (!slug || !email || typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(`${SUBMITTED_IDENTITY_PREFIX}${slug}`, JSON.stringify({
      email,
      name: (identity && identity.name ? String(identity.name) : '').trim(),
    }));
  } catch {
    /* ignore */
  }
}

export function loadSubmittedIdentity(slug) {
  if (!slug || typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(`${SUBMITTED_IDENTITY_PREFIX}${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function loadSubmittedBrief(slug) {
  try {
    const raw = sessionStorage.getItem(`${SUBMITTED_PAYLOAD_PREFIX}${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const EXTERNAL_INTAKE_SECTION_SEEDS = {
  objective:
    'Create clean, engaging creative that highlights the product\'s key benefits while maintaining a premium, on-brand look and feel.',
  brand_guidelines:
    '(Suggested) Pull from current brand guidelines. Keep core palette and logo lockups untouched; new treatments should feel like an evolution, not a re-skin.',
  schedule:
    '(Suggested) Kickoff this week · creative review in 2 weeks · final assets 4 weeks out, ahead of the seasonal flight.',
};

export const EXTERNAL_ASSISTANT_OPENING = `I drafted a full first pass from what you dropped in. Everything tagged "(Suggested)" is my best guess — overwrite freely.

Want me to tighten any of these next?
• the single most important message
• which seasonal moments are in scope
• audience specifics
• exact timeline`;

export function loadVanityDraft(slug) {
  try {
    const raw = sessionStorage.getItem(`brief-vanity-draft:${slug}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const DEFAULT_SECTION_TYPES = [
  { id: 'objective', label: 'Creative direction' },
  { id: 'audience', label: 'Audience' },
  { id: 'key_message', label: 'Key message' },
  { id: 'tone_voice', label: 'Tone & voice' },
  { id: 'brand_guidelines', label: 'Brand guidelines' },
  { id: 'mandatories', label: 'Rules' },
  { id: 'potential_blockers', label: 'Potential blockers' },
  { id: 'objectives', label: 'Objective' },
  { id: 'original_mockup', label: 'Original/Mockup' },
  { id: 'requested_deliverables', label: 'Deliverables' },
  { id: 'references', label: 'References & inspiration' },
  { id: 'deliverables', label: 'Deliverables' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'budget', label: 'Budget' },
  { id: 'success_metrics', label: 'Success metrics' },
];

export const PDP_SECTION_TYPES = [
  { id: 'brand_skus', label: 'Brand & SKUs' },
  { id: 'objective', label: 'The Ask' },
  { id: 'deliverables', label: 'Requested deliverables & specifications' },
  { id: 'creative_direction', label: 'Creative direction' },
  { id: 'mandatories', label: 'Legal mandatories' },
  { id: 'assets_included', label: 'Assets included' },
  { id: 'brand_guidelines', label: 'Brand guidelines' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'audience', label: 'Audience' },
  { id: 'key_message', label: 'Key message' },
  { id: 'tone_voice', label: 'Tone & voice' },
  { id: 'budget', label: 'Budget' },
  { id: 'success_metrics', label: 'Success metrics' },
  { id: 'references', label: 'References & inspiration' },
];

export const PDP_SECTION_LABELS = Object.fromEntries(
  PDP_SECTION_TYPES.map((section) => [section.id, section.label]),
);

export const PDP_SECTION_PLACEHOLDERS = {
  brand_skus: '1. SKU one\n2. SKU two',
  objective: 'Create new PDP assets for the new items that maintain the existing PDP design structure.',
  deliverables: '300x250\n728x90\n300x600\n160x600\n320x50',
  creative_direction: 'Drive a 12% lift in trial during the holiday window.',
  mandatories: 'Legal disclaimers, partner logos, must-show product imagery.',
  assets_included: '- Packaging file\n- Pantone colors\n- Striping file\n- POS file',
  brand_guidelines: 'Brand logo always visible. Primary palette only. No corporate endorsement.',
  schedule: 'Notes (optional) — milestones, review dates, dependencies…',
};

export const PDP_ASSET_SLOT_COUNT = 6;

// Matches the `slot_index` max in BriefReferenceStorage — uploads above it are rejected.
export const PDP_ASSET_SLOT_MAX = 24;

export function getBriefTemplateId(brief) {
  const payload = brief && brief.payload ? brief.payload : {};
  return payload.template || payload.intake_template || null;
}

export function isPdpRefreshBrief(brief) {
  return getBriefTemplateId(brief) === 'pdp';
}

export function getSectionTypesForBrief(brief) {
  return isPdpRefreshBrief(brief) ? PDP_SECTION_TYPES : DEFAULT_SECTION_TYPES;
}

export function getDefaultTitleForTemplate(templateId) {
  if (templateId === 'pdp') {
    return 'PDP refresh';
  }
  if (templateId === 'banner') {
    return 'Banner';
  }
  if (templateId === 'video') {
    return 'Video';
  }
  if (templateId === 'sell_sheet') {
    return 'Sell sheet';
  }
  if (templateId === 'social') {
    return 'Social';
  }
  if (templateId === 'slides') {
    return 'Untitled brief';
  }
  return 'Untitled brief';
}

export function buildBriefCreatePayload(templateId, ccRaw, brandGuideline = null, extras = {}) {
  const payload = {
    template: templateId || null,
    ...extras,
  };
  if (templateId === 'pdp') {
    payload.section_labels = { ...PDP_SECTION_LABELS };
  }
  const ccEmails = parseCcEmails(ccRaw);
  if (ccEmails.length) {
    payload.cc_emails = ccEmails;
  }
  if (brandGuideline && brandGuideline.id != null) {
    payload.brand_guideline_id = brandGuideline.id;
    if (brandGuideline.brand_name) {
      payload.brand_guideline_name = brandGuideline.brand_name;
    }
  }
  return payload;
}

export function parseCcEmails(raw) {
  if (!raw || typeof raw !== 'string') {
    return [];
  }
  return raw
    .split(/[,;\s]+/)
    .map((part) => part.trim().toLowerCase())
    .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
}

export function parseAllowedSenders(raw) {
  if (!raw || typeof raw !== 'string') {
    return [];
  }
  return raw
    .split(/[,;\s]+/)
    .map((part) => part.trim().toLowerCase())
    .filter((value) => /^(?:[^\s@]+@[^\s@]+\.[^\s@]+|[^\s@]+\.[^\s@]+)$/.test(value));
}

const NOTE_SUFFIX =
  'describe the requirements and drop in any assets and we\'ll structure that into a brief.';

export function buildRecipientNote() {
  return `Hi — ${NOTE_SUFFIX}`;
}

export const INTAKE_LINK_TOKEN_BY_BRIEF_PREFIX = 'brief-intake-link-token:';

export const SPECS_SECTION_PLACEHOLDER = `300x250
728x90
300x600
160x600
320x50
970x250`;

export const GENERAL_SECTION_PLACEHOLDERS = {
  objective: 'Drive a 12% lift in trial during the holiday window.',
  objectives: 'Build a waitlist of 25,000 qualified emails before reveal day.',
  audience: 'Millennial parents, ages 28–44, shopping bi-weekly on mobile.',
  brand_guidelines: 'Brand logo always visible. Primary palette only. No corporate endorsement.',
  mandatories: 'Legal disclaimers, partner logos, must-show product imagery.',
  potential_blockers: 'Pending brand approval on final copy. Legal review not scheduled until week 3.',
  original_mockup: 'What are we changing and why? (optional)',
  requested_deliverables: 'Hero banner set, social cutdowns, landing page stills.',
  schedule: 'Notes (optional) — milestones, review dates, dependencies…',
  budget: '$120k production',
  success_metrics: '+18% trial, 2.4x ROAS',
  deliverables: SPECS_SECTION_PLACEHOLDER,
};

export function formatDeliverablesAsSpecsContent(deliverables) {
  if (!Array.isArray(deliverables) || deliverables.length === 0) {
    return '';
  }

  return deliverables.map((d) => {
    const width = d.width;
    const height = d.height;
    if (width && height) {
      return `${width}x${height}`;
    }
    const parts = [d.network, d.placement].filter((part) => part && String(part).trim());
    return parts.join(' · ');
  }).filter(Boolean).join('\n');
}

export function applyLegacyDeliverablesToSections(sections, deliverables) {
  if (!Array.isArray(sections)) {
    return sections;
  }
  const idx = sections.findIndex((s) => s.typeId === 'deliverables');
  if (idx < 0) {
    return sections;
  }
  const section = sections[idx];
  if ((section.content || '').trim()) {
    return sections;
  }
  const legacy = formatDeliverablesAsSpecsContent(deliverables);
  if (!legacy) {
    return sections;
  }
  const next = [...sections];
  next[idx] = { ...section, content: legacy };
  return next;
}

export function getDefaultBriefDetail(id) {
  return {
    id,
    title: 'Untitled brief',
    summary: '',
    status: 'Draft',
    visibility: 'only_me',
    is_owner: false,
    sections: [
      { typeId: 'objective', content: '', visible: true },
      { typeId: 'brand_guidelines', content: '', visible: true },
      { typeId: 'deliverables', content: '', visible: true },
      { typeId: 'schedule', content: '', visible: true },
    ],
    hiddenSectionIds: DEFAULT_SECTION_TYPES.filter(
      (s) => !['objective', 'brand_guidelines', 'deliverables', 'schedule'].includes(s.id),
    ).map((s) => s.id),
  };
}

export const BRIEF_REQUEST_ITEMS = [
  { id: 'target_audience', label: 'Target audience', title: 'Who this is for' },
  { id: 'banner_specs', label: 'Banner specs / sizes', title: 'e.g. 300×250, 728×90, 320×50' },
  { id: 'brand_guidelines', label: 'Brand guidelines', title: '.pdf' },
  { id: 'copy_headlines', label: 'Copy / headlines', title: 'Copy / headlines' },
  { id: 'cta', label: 'CTA', title: 'Button / link text' },
  { id: 'launch_date', label: 'In-market / launch date', title: 'In-market / launch date' },
  { id: 'language', label: 'Language', title: 'e.g. English, Spanish, French' },
  { id: 'fonts', label: 'Fonts', title: '.otf .ttf .woff' },
  { id: 'html5_specs', label: 'HTML5 specs / sizes', title: 'e.g. 300×250, 160×600' },
  { id: 'high_res_logo', label: 'High-res logo', title: '.ai .svg .eps .png' },
  { id: 'legal_disclaimers', label: 'Legal / disclaimers', title: 'Legal / disclaimers' },
  { id: 'pdp_image_sizes', label: 'PDP image sizes', title: 'e.g. 2000×2000 main, 1200×1200 gallery' },
  { id: 'high_res_product_images', label: 'High-res product images', title: 'PNG / JPG, 2000px+' },
  { id: 'retailer', label: 'Retailer', title: 'e.g. Walmart, Target, Kroger' },
  { id: 'retail_media_network', label: 'Retail Media Network', title: 'e.g. Walmart Connect, Roundel, Amazon Ads' },
  { id: 'sku_list', label: 'SKU list', title: 'Product SKUs / IDs to feature' },
  { id: 'video_duration', label: 'Video duration', title: 'e.g. 6s, 15s, 30s' },
  { id: 'video_dimensions', label: 'Video dimensions', title: 'e.g. 1920×1080, 1080×1920, 1080×1080' },
];

export const INVITE_BRIEF_TEMPLATES = [
  {
    id: 'banner',
    label: 'Banner',
    icon: 'image',
    title: 'Display ads across retail media, programmatic, and onsite placements.',
  },
  {
    id: 'video',
    label: 'Video',
    icon: 'video',
    title: 'Short-form, pre-roll, social video, or hero spots.',
  },
  {
    id: 'pdp',
    label: 'PDP refresh',
    icon: 'package',
    title: 'Product detail page imagery — hero, lifestyle, infographics, mobile hero.',
  },
  {
    id: 'sell_sheet',
    label: 'Sell sheet',
    icon: 'newspaper',
    title: 'One-pagers for sales teams, broker decks, and trade shows.',
  },
  {
    id: 'social',
    label: 'Social',
    icon: 'share2',
    title: 'Organic posts, stories, paid social — Instagram, TikTok, Meta, X.',
  },
];

export const INVITE_TEMPLATE_OPTIONS = [
  {
    id: null,
    label: 'Free-form',
    icon: 'sparkles',
    title: 'Recipient picks any template or starts free-form.',
  },
  ...INVITE_BRIEF_TEMPLATES,
];

export const SAVED_TEMPLATE_ID_PREFIX = 't:';

export function encodeSavedTemplateDefaultId(id) {
  if (id == null || id === '') {
    return null;
  }
  return `${SAVED_TEMPLATE_ID_PREFIX}${id}`;
}

export function decodeSavedTemplateDefaultId(value) {
  const raw = String(value || '').trim();
  if (!raw.startsWith(SAVED_TEMPLATE_ID_PREFIX)) {
    return null;
  }
  const id = raw.slice(SAVED_TEMPLATE_ID_PREFIX.length);
  return /^\d+$/.test(id) ? id : null;
}

export function isBuiltInBriefTypeId(value) {
  return INVITE_BRIEF_TEMPLATES.some((tpl) => tpl.id === value);
}

export function isStructuredDefaultBriefType(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return false;
  }
  return isBuiltInBriefTypeId(raw) || Boolean(decodeSavedTemplateDefaultId(raw));
}

/** Formats shown at the top of the Start a brief modal (not templates). */
export const START_BRIEF_FORMATS = [
  {
    id: 'freeform',
    label: 'Free form',
    icon: 'sparkles',
    title: 'Drop in anything — files, links, notes — we\'ll structure it into a brief.',
  },
  {
    id: 'slides',
    label: 'Write slides',
    icon: 'presentation',
    title: 'Write the brief here, slide by slide — add slides as you go.',
  },
  {
    id: 'upload-deck',
    label: 'Upload a deck',
    icon: 'upload',
    title: 'Upload a PowerPoint (.pptx) and pick which slides to bring in.',
  },
];

/** Built-in system templates (user/saved templates are merged in the modal). */
export const START_BRIEF_TEMPLATES = [...INVITE_BRIEF_TEMPLATES];

export const BRIEF_TEMPLATES = START_BRIEF_TEMPLATES;

/**
 * Templates shown in the shared brief create form (`BriefCreateForm.vue`),
 * used by both the logged-in and guest vanity build pages.
 * Saved/user templates are prepended by the page; built-ins render to the right of a divider.
 * `icon` is a lucide name rendered by `BriefsLucideIcon`.
 */
export const CREATE_FORM_TEMPLATES = [
  {
    id: 'blank',
    label: 'Blank',
    icon: 'file-text',
    description: 'Start from scratch. No prompts, no structure — just you and a page.',
  },
  ...INVITE_BRIEF_TEMPLATES.map((tpl) => ({
    id: tpl.id,
    label: tpl.label,
    icon: tpl.icon,
    description: tpl.title,
  })),
];

export const ASSISTANT_SUGGESTIONS = [
  'Draft a holiday campaign for Walmart Connect',
  'Add a Distribution section',
  'Remove Budget',
  'Rename Tone to Voice',
];
